import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { PlateCard } from '../../models/new-release-page.types';
import { flagEmoji } from '../../../names-register/plate-chars';
import { COUNTRY_NAMES } from '../../../names-register/register-fixture';

/** Flags shown before collapsing to "+N" — same cut as the Names register. */
const MAX_FLAGS = 4;

@Component({
  selector: 'pla-new-release-plate-card',
  templateUrl: './new-release-plate-card.component.html',
  styleUrls: ['./new-release-plate-card.component.scss'],
})
export class NewReleasePlateCardComponent {
  @Input() card!: PlateCard;
  /** True when the registration is in the user's notify list (legacy
   *  "Notify" — fires when a sold / unavailable plate comes back). */
  @Input() tracked: boolean = false;
  /** True when the registration is in the user's favourites set. */
  @Input() favourited: boolean = false;
  @Output() trackClicked = new EventEmitter<PlateCard>();
  @Output() favouriteClicked = new EventEmitter<PlateCard>();

  constructor(private router: Router) {}

  /** /compare/:registration expects the registration with no space (e.g. NE76ALL). */
  private regNoSpace(): string {
    return (this.card.plate || '').replace(/\s+/g, '');
  }

  onCardClick(): void {
    const reg = this.regNoSpace();
    if (!reg) return;
    this.router.navigate(['/plate', reg]);
  }

  onTrack(event: MouseEvent): void {
    event.stopPropagation();
    this.trackClicked.emit(this.card);
  }

  onFavourite(event: MouseEvent): void {
    event.stopPropagation();
    this.favouriteClicked.emit(this.card);
  }

  /** Can't be bought right now — legacy greys these and offers Notify
   *  instead of the heart. */
  get isGone(): boolean {
    return this.card.state === 'sold' || this.card.state === 'unavailable';
  }

  /** Legacy parity: Notify on sold / unavailable / auction plates; the
   *  heart on anything you can buy (or watch for auction). */
  get showNotify(): boolean {
    // Pre-release: Notify = "tell me when DVLA puts it on sale".
    return this.isGone || this.card.state === 'auction' || this.card.state === 'prerelease';
  }
  get showFavourite(): boolean {
    return this.card.state !== 'sold';
  }

  formatPrice(): string {
    const { state, price } = this.card;
    if (state === 'auction') return 'Coming to auction';
    // Pre-release: TBC until DVLA publishes its prices, then DVLA's price (the sale opens later).
    if (state === 'prerelease') return price != null ? `From \u00A3${price.toLocaleString('en-GB')}` : 'Price TBC';
    if (state === 'unavailable') return 'Not available';
    if (state === 'sold') return price != null ? `Sold \u00A3${price.toLocaleString('en-GB')}` : 'Sold';
    if (price != null) return `From \u00A3${price.toLocaleString('en-GB')}`;
    return '';
  }

  get badgeLabel(): string | null {
    switch (this.card.state) {
      case 'auction': return 'Auction';
      case 'resale': return 'Resale';
      case 'available': return this.card.newIssue ? 'New issue' : null;
      case 'prerelease':
        // One tag, two strengths: certain (FO/FU/NF tags only ever sell at auction) vs our prediction.
        // Names carry flags under the plate, so they need no tag.
        if (this.card.watch === 'auction_only') return 'Auction only';
        if (this.card.watch === 'auction') return 'Likely auction';
        return null;
      default: return null;
    }
  }

  /** Hover text explaining a pre-release badge. */
  get badgeTitle(): string | null {
    if (this.card.state !== 'prerelease') return null;
    if (this.card.watch === 'auction_only') {
      const tag = (this.card.plate || '').substring(0, 2);
      return `DVLA has only ever sold ${tag} plates at auction, never at a fixed price.`;
    }
    if (this.card.watch === 'auction') return 'Our prediction: DVLA has held this reading back for auction in earlier series.';
    return null;
  }

  get badgeClass(): string | null {
    switch (this.card.state) {
      case 'auction': return 'badge--auction';
      case 'resale': return 'badge--resale';
      case 'available': return this.card.newIssue ? 'badge--new' : null;
      case 'prerelease': return this.card.watch ? 'badge--auction' : null;
      default: return null;
    }
  }

  /** Language flags for name cards (Names register pattern: 4 flags + "+N"). */
  get flags(): Array<{ code: string; flag: string; label: string }> {
    return (this.card.languages || []).slice(0, MAX_FLAGS).map((code) => ({
      code,
      flag: flagEmoji(code),
      label: COUNTRY_NAMES[code] || code.toUpperCase(),
    }));
  }

  get moreLanguages(): number {
    return Math.max(0, (this.card.languages || []).length - MAX_FLAGS);
  }

  get languagesTitle(): string {
    return (this.card.languages || []).map((c) => COUNTRY_NAMES[c] || c.toUpperCase()).join(', ');
  }

  get notifyLabel(): string {
    if (this.card.state === 'prerelease') {
      return this.tracked ? 'On list' : 'Notify me';
    }
    return this.tracked ? 'Notified' : 'Notify';
  }
}
